// Gastos · preparing a receipt ON THE DEVICE (browser only; nothing is sent here). Choosing or opening a file never
// reaches the AI: this only computes what «Leer comprobante» would send, so it can be shown first.
// - The file's SHA-256 (to find the same file already attached; the server computes its own when uploading).
// - A PDF with text: its text (pdf.js), sent as text if Ale chooses to read it, or read locally without AI.
// - A scanned PDF or a photo: up to 3 images reduced to 1600 px (JPEG), which is what an AI reading would send.
// - A preview to show the document next to the proposed data.
import { RECEIPT_MAX_BYTES, receiptTypeOf } from './expenseSchema.js';

export class ReceiptFileError extends Error { constructor(code) { super(code); this.code = code; } }
const MIN_TEXT = 40;
const MAX_PAGES_TEXT = 5;
const MAX_IMAGES = 3;
const MAX_SIDE = 1600;

const hex = (buf) => [...new Uint8Array(buf)].map(x => x.toString(16).padStart(2, '0')).join('');
export async function sha256Of(buffer) { return hex(await crypto.subtle.digest('SHA-256', buffer)); }
function toBase64(buffer) {
  const b = new Uint8Array(buffer); let s = '';
  for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000));
  return btoa(s);
}
const jpegOf = (canvas, quality = 0.85) => canvas.toDataURL('image/jpeg', quality);
const stripPrefix = (dataUrl) => String(dataUrl).split(',')[1] || '';

async function pdfDoc(buffer) {
  let pdfjs;
  try {
    pdfjs = await import('pdfjs-dist');
    const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  } catch { throw new ReceiptFileError('unreadable'); }
  try { return await pdfjs.getDocument({ data: new Uint8Array(buffer.slice(0)), isEvalSupported: false }).promise; }
  catch { throw new ReceiptFileError('unreadable'); }
}
async function renderPage(doc, n, maxSide) {
  const page = await doc.getPage(n);
  const base = page.getViewport({ scale: 1 });
  const scale = Math.min(2, maxSide / Math.max(base.width, base.height));
  const vp = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(vp.width); canvas.height = Math.ceil(vp.height);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  return jpegOf(canvas);
}
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new ReceiptFileError('unreadable')); };
    img.src = url;
  });
}

/**
 * { name, type, size, hash, data (base64 of the original, for uploading), kind: 'pdf_text'|'pdf_scan'|'image',
 *   text, pages, images: [{ type, data }], previewUrl }. Throws ReceiptFileError('file_type'|'file_too_large'|'unreadable').
 */
export async function prepareReceipt(file) {
  const t = receiptTypeOf(file?.name);
  if (!t) throw new ReceiptFileError('file_type');
  if (file.size > RECEIPT_MAX_BYTES) throw new ReceiptFileError('file_too_large');
  const buffer = await file.arrayBuffer();
  const base = { name: file.name.slice(0, 120), type: t.type, size: file.size, hash: await sha256Of(buffer), data: toBase64(buffer) };
  if (t.type === 'application/pdf') {
    const doc = await pdfDoc(buffer);
    const texts = [];
    for (let n = 1; n <= Math.min(doc.numPages, MAX_PAGES_TEXT); n++) {
      const content = await (await doc.getPage(n)).getTextContent();
      texts.push(content.items.map(i => (i.str || '') + (i.hasEOL ? '\n' : ' ')).join('').replace(/[ \t]+/g, ' ').trim());
    }
    const text = texts.join('\n\n').trim();
    const previewUrl = await renderPage(doc, 1, 1100);
    const images = [];
    if (text.length < MIN_TEXT) for (let n = 1; n <= Math.min(doc.numPages, MAX_IMAGES); n++) images.push({ type: 'image/jpeg', data: stripPrefix(await renderPage(doc, n, MAX_SIDE)) });
    const pages = doc.numPages;
    await doc.destroy().catch(() => {});
    return { ...base, kind: text.length >= MIN_TEXT ? 'pdf_text' : 'pdf_scan', text: text.length >= MIN_TEXT ? text : '', pages, images, previewUrl };
  }
  const { img, url } = await loadImage(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale)); canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return { ...base, kind: 'image', text: '', pages: 1, images: [{ type: 'image/jpeg', data: stripPrefix(jpegOf(canvas)) }], previewUrl: url, width: canvas.width, height: canvas.height };
}

/** What «Leer comprobante» would send for this file (shown before sending). */
export function readerInputOf(prepared) {
  if (!prepared) return null;
  if (prepared.kind === 'pdf_text') return { kind: 'text', text: prepared.text };
  return { kind: 'images', images: prepared.images };
}
export function sentDescription(prepared) {
  if (!prepared) return '';
  if (prepared.kind === 'pdf_text') return `el texto del PDF (${prepared.text.length.toLocaleString('es')} caracteres de ${Math.min(prepared.pages, MAX_PAGES_TEXT)} página${Math.min(prepared.pages, MAX_PAGES_TEXT) === 1 ? '' : 's'})`;
  const n = prepared.images.length;
  return `${n === 1 ? 'una imagen' : `${n} imágenes`} del documento, reducida${n === 1 ? '' : 's'} a ${MAX_SIDE} px como máximo`;
}
export const RECEIPT_FILE_MESSAGES = {
  file_type: 'Solo PDF, JPG, PNG o WEBP.', file_too_large: 'El archivo supera 4 MB.',
  unreadable: 'No se pudo abrir el archivo en este dispositivo. Podés adjuntarlo igual y cargar los datos a mano.',
};
